import Combinatorics from 'js-combinatorics'
import { sum, remove, uniq, uniqWith, isEqual, sortBy, cloneDeep, shuffle } from 'lodash'
import { IPlayer, PlayerPosition } from 'protos'

interface AssistedPlayer extends IPlayer {
  isAssisted?: boolean
}

interface TeamAssistantOptions {
  budget?: number
  maximumNumberOfPlayer?: number
  maximumNumberOfGoalkeepers?: number
}

const defaultOptions: TeamAssistantOptions = {
  budget: 16,
  maximumNumberOfPlayer: 4,
  maximumNumberOfGoalkeepers: 1,
}

const isTeamFull = (team: AssistedPlayer[], options: TeamAssistantOptions): boolean => {
  return team.length === Number(options.maximumNumberOfPlayer)
}

const maybeNonGoalkeepers = (team: AssistedPlayer[], options: TeamAssistantOptions) => {
  const maximumNumberOfGoalkeepers = Number(options.maximumNumberOfGoalkeepers)
  const reducer = (v: number, p: AssistedPlayer) => v + Number(p.position === PlayerPosition.PP_GOALKEEPER)

  if (maximumNumberOfGoalkeepers <= team.reduce(reducer, 0)) {
    return (player: AssistedPlayer) => player.position !== PlayerPosition.PP_GOALKEEPER
  } else {
    return () => true
  }
}

const notInTeam = (team: AssistedPlayer[]) => {
  return (player: AssistedPlayer) => team.every(p => p.playerId !== player.playerId)
}

const suggested = (player: AssistedPlayer) => {
  return player.suggested
}

const byCost = (cost: number) => (player: AssistedPlayer) => {
  return player.cost === cost
}

const selectedByUser = (player: AssistedPlayer) => {
  return !player.isAssisted
}

const calculatePossibleCostVariations = (
  players: AssistedPlayer[],
  currentTeam: AssistedPlayer[],
  options: TeamAssistantOptions,
): Array<number[]> => {
  const value = currentTeam.reduce((v, p) => v + Number(p.cost), 0)
  const budgetFilter = (row: number[]) => sum(row) <= Number(options.budget) - value
  const costs = uniq(players.map(player => Number(player.cost)))
  const length = Number(options.maximumNumberOfPlayer) - currentTeam.length
  const baseN = Combinatorics.baseN(costs, length).filter(budgetFilter).map(row => row.sort())

  return sortBy(uniqWith(baseN, isEqual), sum).reverse()
}

export default (
  players: AssistedPlayer[] = [],
  currentTeam: AssistedPlayer[] = [],
  options?: TeamAssistantOptions,
): AssistedPlayer[] => {
  options = Object.assign(cloneDeep(defaultOptions), options)
  players = shuffle(players)

  const userSelectedTeam = currentTeam.filter(selectedByUser)
  const filteredPlayers = players.filter(suggested).filter(notInTeam(userSelectedTeam))
  const costVariations = calculatePossibleCostVariations(filteredPlayers, userSelectedTeam, options)

  for (let costVariation of costVariations) {
    const assistedTeam = cloneDeep(userSelectedTeam)
    const playersClone = cloneDeep(filteredPlayers)

    for (let cost of costVariation) {
      const player = playersClone.filter(maybeNonGoalkeepers(assistedTeam, options)).find(byCost(cost))

      if (player) {
        assistedTeam.push({ isAssisted: true, ...player })
        remove(playersClone, p => p.playerId === player.playerId)
      }
    }

    if (isTeamFull(assistedTeam, options)) {
      return assistedTeam
    }
  }

  throw new Error('not found possible team variation')
}
